import React, { useState } from 'react';
import { Button, Dropdown, message } from 'antd';
import { DownloadOutlined, FileExcelOutlined, FilePdfOutlined } from '@ant-design/icons';

/**
 * ExportButton - Button component for exporting data to Excel/PDF
 * @param {Object} props
 * @param {string} props.reportType - Type of report to export
 * @param {Object} props.filters - Filter values to include in export
 * @param {boolean} props.disabled - Whether the button is disabled
 */
function ExportButton({ reportType, filters, disabled = false }) {
  const [loading, setLoading] = useState(false);

  /**
   * Build export URL with query parameters
   */
  const buildExportUrl = (format = 'excel') => {
    const baseUrl = 'https://8siu8hczc2.execute-api.us-east-1.amazonaws.com/Prod/skusRecords';
    const params = new URLSearchParams();

    params.append('reportType', reportType);
    params.append('documentGenerate', 'true');

    // Add date range
    if (filters.dateRange?.initialDate) {
      params.append('TimeStart', filters.dateRange.initialDate);
    }
    if (filters.dateRange?.finalDate) {
      params.append('TimeEnd', filters.dateRange.finalDate);
    }

    // Add boolean filters
    if (filters.groupByNit !== undefined) {
      params.append('groupByNit', filters.groupByNit.toString());
    }
    if (filters.onlyActive !== undefined) {
      params.append('onlyActive', filters.onlyActive.toString());
    }

    // Add NIT filters
    if (filters.nitList && filters.nitList.length > 0) {
      filters.nitList.forEach(nit => {
        params.append('nit', nit);
      });
    } else if (filters.nit && filters.nit !== '1') {
      params.append('nit', filters.nit);
    }

    // Add articleType filter
    if (filters.articleType && filters.articleType.length > 0) {
      params.append('articleType', filters.articleType.join(','));
    }

    // Add category filter
    if (filters.category && filters.category.length > 0) {
      params.append('category', filters.category.join(','));
    }

    // Add sorting
    if (filters.sortBy) {
      params.append('sortBy', filters.sortBy);
    }
    if (filters.order) {
      params.append('order', filters.order);
    }

    // Add format
    params.append('format', format);

    return `${baseUrl}?${params.toString()}`;
  };

  /**
   * Handle export to Excel
   */
  const handleExportExcel = async () => {
    setLoading(true);
    try {
      const url = buildExportUrl('excel');
      const link = document.createElement('a');
      link.href = url;
      link.download = `reporte_${reportType}_${new Date().getTime()}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      message.success('Reporte exportado exitosamente');
    } catch (error) {
      message.error('Error al exportar el reporte');
      console.error('Export error:', error);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Handle export to PDF
   */
  const handleExportPdf = async () => {
    setLoading(true);
    try {
      const url = buildExportUrl('pdf');
      const link = document.createElement('a');
      link.href = url;
      link.download = `reporte_${reportType}_${new Date().getTime()}.pdf`;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      message.success('Reporte exportado exitosamente');
    } catch (error) {
      message.error('Error al exportar el reporte');
      console.error('Export error:', error);
    } finally {
      setLoading(false);
    }
  };

  const exportItems = [
    {
      key: 'excel',
      icon: <FileExcelOutlined />,
      label: 'Exportar a Excel',
      onClick: handleExportExcel
    },
    {
      key: 'pdf',
      icon: <FilePdfOutlined />,
      label: 'Exportar a PDF',
      onClick: handleExportPdf
    }
  ];

  return (
    <Dropdown
      menu={{ items: exportItems }}
      trigger={['click']}
      disabled={disabled || loading}
    >
      <Button
        icon={<DownloadOutlined />}
        loading={loading}
        disabled={disabled}
      >
        Exportar
      </Button>
    </Dropdown>
  );
}

export default ExportButton;
